/**
 * VConsole 管理器
 * 统一管理移动端调试工具的初始化和配置
 */

import { VCONSOLE_DEFAULT_STATIC_OPTIONS } from "@/utils/vconsole/vconsoleDefaults";
import type { VConsoleSerializableConfig } from "@/utils/vconsole/vconsoleDefaults";
import { parseVConsoleRuntimeOptions } from "@/utils/vconsole/vconsoleRuntimeOptions";

let vconsoleUmdScriptPromise: Promise<void> | null = null;

/** 动态插入 public/assets/js/vconsole.min.js（UMD），与 html bootstrap 共用，只加载一次 */
export function ensureVConsoleUmdScriptLoaded(): Promise<void> {
  const w = window as unknown as { VConsole?: unknown };
  if (typeof w.VConsole === "function") {
    return Promise.resolve();
  }
  vconsoleUmdScriptPromise ??= new Promise<void>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = `${import.meta.env.BASE_URL}assets/js/vconsole.min.js`;
    el.async = false;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error("Failed to load js/vconsole.min.js"));
    document.head.appendChild(el);
  }).finally(() => {
    vconsoleUmdScriptPromise = null;
  });
  return vconsoleUmdScriptPromise;
}

interface VConsoleInitConfig extends VConsoleSerializableConfig {
  onReady?: () => void;
  onClearLog?: () => void;
}

type ConsoleMethodName = "log" | "info" | "warn" | "error" | "debug" | "trace";
type NativeConsoleSnapshot = Record<ConsoleMethodName, (...args: unknown[]) => void>;
type VConsoleInstance = { destroy: () => void };
type VConsoleWindowBridge = {
  __VCONSOLE_BOOTSTRAPPED__?: boolean;
  __VCONSOLE_NATIVE_CONSOLE__?: NativeConsoleSnapshot;
};

class VConsoleManager {
  private vConsoleInstance: VConsoleInstance | null = null;
  private isInitialized = false;
  private nativeConsoleSnapshot: NativeConsoleSnapshot | null = null;

  private shouldRestoreNativeConsoleForSourceTrace(): boolean {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("vconsole_source") ?? localStorage.getItem("vconsole_source") ?? "false";
    return /^(1|true|yes|on)$/i.test(String(raw));
  }

  private snapshotNativeConsoleMethods(): void {
    if (this.nativeConsoleSnapshot) {
      return;
    }
    const w = window as unknown as VConsoleWindowBridge;
    if (w.__VCONSOLE_NATIVE_CONSOLE__) {
      this.nativeConsoleSnapshot = w.__VCONSOLE_NATIVE_CONSOLE__;
      return;
    }
    this.nativeConsoleSnapshot = {
      log: console.log.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      debug: console.debug.bind(console),
      trace: console.trace.bind(console),
    };
  }

  private restoreNativeConsoleMethods(): void {
    if (!this.nativeConsoleSnapshot) {
      return;
    }
    const methods: ConsoleMethodName[] = ["log", "info", "warn", "error", "debug", "trace"];
    methods.forEach((method) => {
      console[method] = this.nativeConsoleSnapshot![method];
    });
  }

  /**
   * 检查是否应该启用 VConsole
   */
  public shouldEnable(): boolean {
    // 新增：PC浏览器直接不启用，避免影响调试
    const isPCBrowser = !/Android|iPhone|iPad|iPod|iOS|BlackBerry|Windows Phone/i.test(
      navigator.userAgent
    );
    if (isPCBrowser) {
      return false;
    }
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("vconsole") === "true") {
      return true;
    }
    // 与 main.ts 中非 production 自动拉起逻辑一致（否则仅 dev/test 调用 initialize 也会被拦截）
    if (["dev", "test"].includes(import.meta.env.MODE)) {
      return true;
    }

    return false;
  }

  /**
   * 初始化 VConsole
   */
  async initialize(config: VConsoleInitConfig = {}): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    const shouldRestoreNativeConsole = this.shouldRestoreNativeConsoleForSourceTrace();
    const wBoot = window as unknown as VConsoleWindowBridge;
    if (wBoot.__VCONSOLE_BOOTSTRAPPED__) {
      if (shouldRestoreNativeConsole) {
        this.snapshotNativeConsoleMethods();
        this.restoreNativeConsoleMethods();
      }
      this.isInitialized = true;
      return;
    }
    if (!this.shouldEnable()) {
      return;
    }

    try {
      if (shouldRestoreNativeConsole) {
        // 恢复原生 console 便于桌面 DevTools 定位业务源码；vConsole Log 依赖对 console 的劫持，此模式下面板通常捕不到日志
        this.snapshotNativeConsoleMethods();
      }
      const runtimeConfig = parseVConsoleRuntimeOptions();

      const defaultConfig: VConsoleInitConfig = {
        ...VCONSOLE_DEFAULT_STATIC_OPTIONS,
        ...runtimeConfig,
        ...config,
      };

      const w = window as unknown as {
        VConsole?: new (opts?: VConsoleInitConfig) => VConsoleInstance;
      };

      if (typeof w.VConsole === "function") {
        const VConsoleClass = w.VConsole;
        this.vConsoleInstance = new VConsoleClass(defaultConfig);
      } else {
        await ensureVConsoleUmdScriptLoaded();
        if (typeof w.VConsole === "function") {
          this.vConsoleInstance = new (w.VConsole as new (
            opts?: VConsoleInitConfig
          ) => VConsoleInstance)(defaultConfig);
        }
      }

      this.isInitialized = true;
      if (shouldRestoreNativeConsole) {
        // 开启后 DevTools 栈更准，但 vConsole「Log」里通常不再有输出
        this.restoreNativeConsoleMethods();
      }
    } catch (error) {
      console.error("❌ VConsole 初始化失败:", error);
      // 确保即使失败也不会阻塞应用
      this.isInitialized = false;
      if (shouldRestoreNativeConsole) {
        this.restoreNativeConsoleMethods();
      }
    }
  }

  /**
   * 销毁 VConsole
   */
  destroy(): void {
    if (this.vConsoleInstance) {
      this.vConsoleInstance.destroy();
      this.vConsoleInstance = null;
      this.isInitialized = false;
    }
  }

  /**
   * 获取 VConsole 实例
   */
  getInstance() {
    return this.vConsoleInstance;
  }

  /**
   * 检查是否已初始化
   */
  isReady(): boolean {
    return this.isInitialized && this.vConsoleInstance !== null;
  }
}

// 导出单例
export const vConsoleManager = new VConsoleManager();
